import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Delete, ModeEdit } from '@mui/icons-material';
import { Box, CircularProgress, SpeedDialAction } from '@mui/material';
import { observer } from 'mobx-react-lite';
import {
  AdjustmentsList,
  AdvancesList,
  AssetsList,
  CompanyInformation,
  ConfirmModal,
  Documents,
  FinancialOverview,
  FuseTabs,
  LoansList,
  Notes,
  PaymentsList,
  ViewPage,
} from '/src/components';
import { useCompanyStore } from '/src/context';
import { toastMessages } from '/src/lib/messages';
import { Note } from '/src/lib/models';
import { CRUDMode, DataModelType } from '/src/lib/types';
import { theme } from '/src/theme';

/**
 * View Asset Page
 */
export const ViewCompanyPage = observer(() => {
  /** Context **/
  const companyStore = useCompanyStore();
  const params = useParams();
  const navigate = useNavigate();

  /** State **/
  const [showConfirmDeactivate, setShowConfirmDeactivate] = useState(false);

  /**
   * Array of booleans indicating which sidebar items are open.
   *
   * @remarks
   * Each item's index in the array corresponds to its position in the sidebar.
   */
  const [sidebarItems, setSidebarItems] = useState<boolean[]>([
    true, // Financial Overview (open by default)
    false, // Company Information
    false, // Notes
    false, // Documents
  ]);

  /** Computed **/
  const company = companyStore.currentCompany;
  const assetListOptions = {
    value: true,
    originalValue: true,
    currentValue: true,
    program: true,
    status: true,
    totalPayments: true,
    totalAdjustments: true,
    loan: true,
  };

  /** Methods **/

  /**
   * Deactivates the Company.
   *
   * @remarks
   * We don't ever remove Companies from the system, just mark them as "inactive".
   *
   * @remarks
   * Redirects to the Companies List afterwards.
   */
  const deactivate = async () => {
    if (!company) return;

    try {
      await companyStore.deactivateCompany(company?._id);

      // Show alert
      toast.success(toastMessages.deleteCompany.success);

      // Redirect to assets
      navigate('/companies');
    } catch (err) {
      console.error(err);
      toast.error(toastMessages.deleteCompany.error);
    }
  };

  /**
   * Updates the Notes list.
   */
  const updateNotes = async (notes: Note[]) => {
    if (!company) return;

    const response = await companyStore.update({ ...company, notes });
    if (!response) throw new Error(`Error updating notes for Company ID ${company._id}`);

    companyStore.currentCompany = await companyStore.get(company._id);
  };

  /**
   * Updates the Documents list.
   *
   * @param files List of Document URLs.
   */
  const updateFiles = async (files: string[]) => {
    if (!company) return;

    const response = await companyStore.update({ ...company, files });
    if (!response) throw new Error(`Error updating files for Company ID ${company._id}`);

    companyStore.currentCompany = await companyStore.get(company._id);
  };

  /** Effects **/

  useEffect(() => {
    params.companyId && companyStore.get(params.companyId);
  }, [params.companyId]);

  /** Render **/
  return (
    <ViewPage
      modelName={company?.name ?? ''}
      modelType={DataModelType.Company}
      sidebar={
        <>
          <FinancialOverview
            company={company}
            open={sidebarItems[0]}
            toggleOpen={() => setSidebarItems(sidebarItems.with(0, !sidebarItems[0]))}
          />
          <CompanyInformation
            company={company}
            open={sidebarItems[1]}
            toggleOpen={() => setSidebarItems(sidebarItems.with(1, !sidebarItems[1]))}
          />
          <Notes
            mode={CRUDMode.View}
            notes={company?.notes ?? []}
            title="Notes"
            tooltip="Add Note"
            sidebarIndex={2}
            sidebarItems={sidebarItems}
            setSidebarItems={setSidebarItems}
            updateNotes={updateNotes}
          />
          <Documents
            mode={CRUDMode.Edit}
            documents={company?.files ?? []}
            sidebarIndex={3}
            sidebarItems={sidebarItems}
            setSidebarItems={setSidebarItems}
            updateDocuments={updateFiles}
          />
        </>
      }
      speedDialActions={[
        <SpeedDialAction
          key="edit-button"
          className="md-fab-action-item"
          icon={<ModeEdit className="material-icons" />}
          slotProps={{
            fab: {
              'aria-label': 'Edit Company',
              className: 'md-fab md-mini md-accent md-button md-default-theme',
            },
            tooltip: {
              title: 'Edit Company',
            },
          }}
          onClick={() => navigate(`/companies/${company?._id}/edit`)}
        />,
        <SpeedDialAction
          key="delete-button"
          className="md-fab-action-item"
          icon={<Delete className="material-icons" />}
          slotProps={{
            fab: {
              'aria-label': 'Deactivate Company',
              className: 'md-fab md-mini md-warn md-button md-default-theme',
              style: { background: theme.palette.warning.main, color: 'white' },
            },
            tooltip: {
              title: 'Deactivate Company',
            },
          }}
          onClick={() => setShowConfirmDeactivate(true)}
        />,
      ]}
      modals={
        <ConfirmModal
          content="Are you sure you want to deactivate this Company? Doing so will deactivate its associated loans and assets as well."
          open={showConfirmDeactivate}
          setOpen={setShowConfirmDeactivate}
          onOk={() => deactivate()}
          label="Confirm Company Deactivation"
          okLabel="Deactivate"
          cancelLabel="Cancel"
        />
      }
    >
      {company ? (
        <FuseTabs defaultTab={0}>
          {[
            {
              index: 0,
              label: 'Advances',
              content: company ? <AdvancesList query={{ companyId: company._id }} /> : <></>,
            },
            {
              index: 1,
              label: 'Payments',
              content: company ? <PaymentsList query={{ companyId: company._id }} /> : <></>,
            },
            {
              index: 2,
              label: 'Assets',
              content: company ? (
                <AssetsList query={{ companyId: company._id }} viewOptions={assetListOptions} />
              ) : (
                <></>
              ),
            },
            {
              index: 3,
              label: 'Adjustments',
              content: company ? <AdjustmentsList query={{ companyId: company._id }} /> : <></>,
            },
            {
              index: 4,
              label: 'Deals',
              content: company ? <LoansList query={{ companyId: company._id }} /> : <></>,
            },
          ]}
        </FuseTabs>
      ) : (
        <Box display="flex" flex="100" justifyContent="center" alignItems="center" className="pad-top-bottom">
          <CircularProgress className="md-primary" md-mode="indeterminate" />
        </Box>
      )}
    </ViewPage>
  );
});
